<template>
  <div class="kolomoisky-properties p-3"
       :class="{
         'kolomoisky-properties--social-mode': socialMode,
         'kolomoisky-properties--cluster-selected': showSelectedCluster,
       }">
    <h4>Ihor Kolomoisky's U.S. shopping spree</h4>
    <p>Ihor Kolomoisky and associates purchased 22 properties between 2006 and 2015, mostly through companies registered in Delaware. More than $490 million was siphoned from PrivatBank, a Ukraine bank Kolomoisky owned, towards the purchase of some of these properties, court records and interviews show.</p>
    <div class="kolomoisky-properties__panel" :style="{ height: `${mapHeight}px` }" v-if="showSelectedCluster">
      <div class="kolomoisky-properties__panel__header d-flex">
        <div class="px-2 py-1 ">
          Ihor Kolomoisky's properties in that area
        </div>
        <a @click="toggleSelectedCluster(false)" class="ml-auto px-2 py-1 text-white">
          <fa icon="times" />
        </a>
      </div>
      <div class="p-4">
        <div class="kolomoisky-properties__panel__properties">
          <div v-for="[city, properties] in selectedClusterPropertiesByCity" :key="city" class="mb-4">
            <h5>{{ city }}</h5>
            <div v-for="(property, index) in properties"
                 :key="index"
                 class="kolomoisky-properties__panel__properties__item mb-2 pb-3"
                 :class="{
                   [`kolomoisky-properties__panel__properties__item--${kebabCase(property.category)}`]: true
                 }">
              <div class="kolomoisky-properties__panel__properties__item__header mb-2">
                <h6 class="kolomoisky-properties__panel__properties__item__header__name font-weight-bold p-4 m-0">
                  <fa :icon="property.category | categoryIcon" fixed-width size="lg" class="mr-2" />
                  {{ property.name }}
                </h6>
              </div>
              <div class="px-4 d-flex align-items-center">
                <div class="d-flex flex-grow-1 align-items-center ">
                  <div class="flex-grow-1">
                    <div class="small d-flex text-nowrap">
                      <div class="flex-grow-1 mb-2">
                        Purchased
                        <div class="font-weight-bold">
                          {{ property.purchase_price }}
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        Invested
                        <div class="font-weight-bold">
                          {{ property.amount_invested }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="kolomoisky-properties__panel__properties__item__notes px-4" v-if="property.notes !== ''">
                *{{ property.notes }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <svg class="kolomoisky-properties__map" :class="{ [`kolomoisky-properties__map--${mapSize}`]: true }" width="100%" :height="mapHeight" @click="toggleSelectedCluster(false)">
      <g class="kolomoisky-properties__map__states" :transform="`translate(${padding}, ${padding})`"></g>
      <g class="kolomoisky-properties__map__properties" :transform="`translate(${padding}, ${padding})`">
        <g v-for="cluster in propertyClusters"
           :key="cluster.id"
           class="kolomoisky-properties__map__properties__cluster"
           :class="{ 'kolomoisky-properties__map__properties__cluster--selected': cluster.id === selectedCluster }"
           :transform="`translate(${projection(cluster.geometry.coordinates)})`">
          <rect class="kolomoisky-properties__map__properties__cluster__caret"
                x="-3"
                y="-3"
                width="6"
                height="6"
                transform="rotate(45 0 0)" />
          <foreignObject x="-200" y="-200" width="400" height="400">
            <div class="h-100 d-flex justify-content-center align-items-center">
              <ul class="list-inline kolomoisky-properties__map__properties__cluster__leaves m-0"
                  @click.stop="selectCluster(cluster.id)"
                  :class="{
                    'kolomoisky-properties__map__properties__cluster__leaves--flip-y': false,
                    [`kolomoisky-properties__map__properties__cluster__leaves--4n${propertyClusterCategories(cluster.id).length % 4}`]: true
                  }">
                <li v-for="[category, leaves] in propertyClusterLeavesByCategory(cluster.id)"
                    :key="category"
                    :title="category"
                    :class="{ [`kolomoisky-properties__map__properties__cluster__leaves__item--${kebabCase(category)}`]: true }"
                    class="list-inline-item kolomoisky-properties__map__properties__cluster__leaves__item">
                  <span class="kolomoisky-properties__map__properties__cluster__leaves__item__value" v-if="leaves.length > 1">
                    {{ leaves.length }}
                  </span>
                  <fa :icon="category | categoryIcon" fixed-width />
                </li>
              </ul>
            </div>
          </foreignObject>
        </g>
      </g>
    </svg>
    <div class="kolomoisky-properties__categories">
      <h6 class="mb-0">Property types</h6>
      <ul class="list-unstyled">
        <li v-for="category in categories"
            :key="category"
            class="kolomoisky-properties__categories__item"
            :class="{ [`kolomoisky-properties__categories__item--${kebabCase(category)}`]: true }">
          <fa :icon="category | categoryIcon" class="kolomoisky-properties__categories__item__icon" fixed-width />
          {{ category }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import Supercluster from 'supercluster'

import flow from 'lodash/fp/flow'
import groupBy from 'lodash/fp/groupBy'
import sortBy from 'lodash/fp/sortBy'
import toPairs from 'lodash/fp/toPairs'

import camelCase from 'lodash/camelCase'
import kebabCase from 'lodash/kebabCase'
import uniq from 'lodash/uniq'

import { faIndustry, faCity, faBuilding, faBed } from '@fortawesome/free-solid-svg-icons'

import chart from '../mixins/chart'
import states from '../assets/us-states.json'
import propertiesTsv from '!raw-loader!../assets/properties.tsv' // eslint-disable-line import/no-webpack-loader-syntax

export default {
  name: 'KolomoiskyProperties',
  mixins: [chart],
  props: {
    /**
       * Padding space around the map (in px)
       */
    padding: {
      type: Number,
      default: 30
    },
    /**
       * Zoom level for cluster computation
       */
    zoom: {
      type: Number,
      default: 4
    },
    /**
        * Radius of each property cluster
        */
    clusterRadius: {
      type: Number,
      default: 30
    }
  },
  data () {
    return {
      mapHeight: 50,
      showSelectedCluster: false,
      selectedCluster: null
    }
  },
  filters: {
    categoryIcon (category) {
      return {
        factory: faIndustry,
        hotel: faBed,
        officeComplex: faCity,
        officeTower: faBuilding,
        skyscraper: faBuilding
      }[camelCase(category)]
    }
  },
  mounted () {
    this.$on('resized', () => {
      this.setMapHeight()
      this.redrawMap()
    })
  },
  watch: {
    mounted () {
      this.setMapHeight()
      this.drawMap()
    },
    socialMode () {
      this.setMapHeight()
      this.redrawMap()
    }
  },
  computed: {
    map () {
      return d3.select(this.$el).select('.kolomoisky-properties__map')
    },
    mapWidth: {
      cache: false,
      get () {
        return !this.mounted ? 100 : this.$el.offsetWidth
      }
    },
    mapSize () {
      switch (true) {
        case this.mapHeight < 300: return 'xs'
        case this.mapHeight < 350: return 'sm'
        case this.mapHeight < 400: return 'md'
        case this.mapHeight < 450: return 'lg'
        default: return 'xl'
      }
    },
    mapPaddedWidth () {
      return this.mapWidth - this.padding * 2
    },
    mapPaddedHeight () {
      return this.mapHeight - this.padding * 2
    },
    geojson () {
      return topojson.feature(states, states.objects.states)
    },
    projection () {
      return d3.geoAlbers().fitSize([this.mapPaddedWidth, this.mapPaddedHeight], this.propertyFeatureCollection)
    },
    path () {
      return d3.geoPath(this.projection)
    },
    properties () {
      return sortBy('category')(d3.tsvParse(propertiesTsv))
    },
    categories () {
      return uniq(this.properties.map(p => p.category))
    },
    propertyFeatureCollection () {
      const features = this.properties.map(property => {
        const { longitude, latitude, ...properties } = property
        return {
          type: 'Feature',
          geometry: {
            properties,
            type: 'Point',
            coordinates: [longitude, latitude]
          }
        }
      })
      return { type: 'FeatureCollection', features }
    },
    propertyIndex () {
      const index = new Supercluster({ radius: this.clusterRadius, maxZoom: 16, minPoints: 1 })
      const { features } = this.propertyFeatureCollection
      return index.load(features)
    },
    propertyClusters () {
      const bounds = this.path.bounds(this.geojson)
      return this.propertyIndex.getClusters(bounds.flat(), this.zoom)
    },
    selectedClusterProperties () {
      if (this.selectedCluster === null) {
        return []
      }
      return this.propertyClusterLeaves(this.selectedCluster).map(c => c.geometry.properties)
    },
    selectedClusterPropertiesByCity () {
      return flow(
        groupBy(property => property.city),
        toPairs,
        sortBy(([city]) => city)
      )(this.selectedClusterProperties)
    }
  },
  methods: {
    kebabCase,
    drawMap () {
      this.map.select('g.kolomoisky-properties__map__states')
        .selectAll('path')
        .data(this.geojson.features)
        .enter()
        .append('path')
        .attr('d', this.path)
    },
    redrawMap () {
      this.map.select('g.kolomoisky-properties__map__states')
        .selectAll('path')
        .attr('d', this.path)
    },
    propertyClusterCategories (clusterId) {
      return this.propertyClusterLeavesByCategory(clusterId).map(([category]) => category)
    },
    propertyClusterLeaves (clusterId) {
      return this.propertyIndex.getLeaves(clusterId, Infinity)
    },
    propertyClusterLeavesByCategory (clusterId) {
      return flow(
        this.propertyClusterLeaves,
        groupBy(leaf => leaf.geometry.properties.category),
        toPairs,
        sortBy(([category, leaves]) => leaves.length)
      )(clusterId)
    },
    setMapHeight () {
      const height = this.mapWidth * (this.socialMode ? 4 / 5 : 9 / 16)
      this.mapHeight = height - this.titleAndDescriptionHeight
    },
    selectCluster (clusterId) {
      this.toggleSelectedCluster(this.selectedCluster !== clusterId)
      this.selectedCluster = this.selectedCluster !== clusterId ? clusterId : null
    },
    toggleSelectedCluster (toggle = null) {
      this.showSelectedCluster = toggle !== null ? toggle : !this.showSelectedCluster
    }
  }
}
</script>

<style lang="scss">
  @import '../../node_modules/bootstrap/scss/mixins/_gradients.scss';

  .kolomoisky-properties {

    --category-factory: #236243;
    --category-hotel: #41ad49;
    --category-office-complex: #999078;
    --category-office-tower: #85754e;
    --category-skyscraper: #e0cb6c;

    position: relative;

    &__panel {
      max-width: 440px;
      width: 40%;
      min-width: 350px;
      background: repeating-linear-gradient(-55deg, #fff, #fff 1px, #ebebeb 3px, #ebebeb 3px);
      position: absolute;
      z-index: 200;
      overflow: auto;

      .kolomoisky-properties--social-mode & {
        background: darken(#212529, 5);
      }

      &__header {
        position: sticky;
        top: 0;
        left: 0;
        z-index: 100;
        background: black;
        color: white;

        a {
          cursor: pointer;
        }
      }

      &__properties {

        &__item {
          color: white;

          &__header {
            position: relative;

            &:after {
              content:"";
              width: 60px;
              height: 2px;
              background: white;
              position: absolute;
              left: 1.5rem;
              bottom: .5rem;
            }
          }

          &__notes {
            font-size: 0.7em;
          }

          &--factory {
            background: var(--category-factory);
          }

          &--hotel {
            background: var(--category-hotel);
          }

          &--office-complex {
            background: var(--category-office-complex);
          }

          &--office-tower {
            background: var(--category-office-tower);
          }

          &--skyscraper {
            background: var(--category-skyscraper);
          }
        }
      }
    }

    &__map {
      position: relative;
      z-index: 100;

      &--xs { font-size: 0.6rem; }
      &--sm { font-size: 0.7rem; }
      &--md { font-size: 0.8rem; }
      &--lg { font-size: 0.9rem; }
      &--xl { font-size: 1rem; }

      &__states {
        fill: #e0e0e0;
        stroke: #f2f2f2;
        stroke-width: 1px;
        stroke-linejoin: round;
        stroke-linecap: round;
        pointer-events: none;

        .kolomoisky-properties--social-mode & {
          fill: darken(#212529, 10);
          stroke: #212529;
        }
      }

      &__properties {

        &__cluster {
          pointer-events: none;
          filter: drop-shadow(5px 0 5px rgba(black, 0.4));

          .kolomoisky-properties--cluster-selected &:not(&--selected) {
            filter: grayscale(50%);
            opacity: .5;
          }

          &__caret {
            fill: black;

            .kolomoisky-properties--social-mode & {
              fill: white;
            }
          }

          &__leaves {
            pointer-events: all;
            display: block;
            background: black;
            border: 1px solid black;
            border-radius: 1em;
            overflow: hidden;
            transform: translateX(50%);
            float: left;
            cursor: pointer;

            .kolomoisky-properties--social-mode & {
              background: white;
              border: 1px solid white;
            }

            &--flip-y {
              transform: translateX(-50%);
            }

            & &__item, &  &__item:not(:last-child) {
              margin: 0;
            }

            &__item {
              color: white;
              padding: 0 0.5em;
              display: flex;
              white-space: nowrap;
              text-align: center;

              &__value {
                font-weight: bolder;
              }

              &--factory {
                background: var(--category-factory);
              }

              &--hotel {
                background: var(--category-hotel);
              }

              &--office-complex {
                background: var(--category-office-complex);
              }

              &--office-tower {
                background: var(--category-office-tower);
              }

              &--skyscraper {
                background: var(--category-skyscraper);
              }
            }
          }
        }
      }
    }

    &__categories {
      position: absolute;
      z-index: 110;
      right: 1rem;
      bottom: 1rem;

      &__item {

        &--factory &__icon {
          color: var(--category-factory);
        }

        &--hotel &__icon {
          color: var(--category-hotel);
        }

        &--office-complex &__icon {
          color: var(--category-office-complex);
        }

        &--office-tower &__icon {
          color: var(--category-office-tower);
        }

        &--skyscraper &__icon {
          color: var(--category-skyscraper);
        }
      }
    }

  }
</style>
